/**
 * The big screen's auto-invite (GH-864, owner amendment 3; GH-864e).
 *
 * The display's QR carries `?join=<token>` (`EVENT_INVITE_QUERY_PARAM`): a stateless signed token,
 * about ten minutes long, valid only while the event is open. Anyone who scans it and signs in from
 * that flow becomes a member at once, skipping the waitlist. A token that has lapsed is not an
 * error — the server puts the donor on the waitlist and this card says "Ask a member for an invite".
 *
 * The token is read from the address once, checked for shape, and taken **out** of the address
 * (`history.replaceState`), so a link copied from this tab or a screenshot of it does not carry the
 * auto-invite on. It is never stored.
 *
 * Nothing here touches giving or check-in: the card sits above the page's own content and the
 * page works the same with it, without it, and with the gate off (the card is simply not drawn).
 */
import { useState } from "react";
import {
  EVENT_INVITE_QUERY_PARAM,
  eventInviteTokenSchema,
  MEMBERSHIP_COPY,
  type DonorSessionResponse,
} from "@givehere/contracts";
import { DonorApiError, fetchDonorMembership, joinFromEvent } from "./donor-session.js";
import { useMembershipPulse } from "./membership-hooks.js";
import { grouped } from "./share.js";
import { SignInSheet } from "./SignInSheet.js";

/** The auto-invite token in this address, if it has a token's shape; and strip it from the bar. */
export function takeEventJoinToken(
  location: Pick<Location, "search" | "pathname" | "hash"> = window.location,
): string | null {
  const query = new URLSearchParams(location.search);
  const raw = query.get(EVENT_INVITE_QUERY_PARAM);
  if (raw === null) return null;
  query.delete(EVENT_INVITE_QUERY_PARAM);
  try {
    const rest = query.toString();
    window.history.replaceState(
      window.history.state,
      "",
      `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`,
    );
  } catch {
    // An address that cannot be rewritten keeps the token; it lapses on its own in minutes.
  }
  const parsed = eventInviteTokenSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

type JoinState =
  | { kind: "idle" }
  | { kind: "joining" }
  | { kind: "joined"; number: number; founding: boolean }
  | { kind: "already" }
  | { kind: "waitlisted" }
  | { kind: "failed"; message: string }
  | { kind: "gone" };

/** The join itself, shared by the card's button and a host page's own sign-in (`/c/<eventId>`). */
export function useEventJoin(joinToken: string | null) {
  const [state, setState] = useState<JoinState>({ kind: "idle" });
  const join = async (token: string) => {
    if (!joinToken) return;
    setState({ kind: "joining" });
    try {
      const answer = await joinFromEvent(token, joinToken);
      const member = answer.membership.member;
      setState(
        answer.outcome === "joined" && member
          ? { kind: "joined", number: member.number, founding: member.founding }
          : answer.outcome === "waitlisted"
            ? { kind: "waitlisted" }
            : { kind: "already" },
      );
    } catch (caught: unknown) {
      setState(
        caught instanceof DonorApiError && caught.status === 404
          ? { kind: "gone" }
          : {
              kind: "failed",
              message:
                caught instanceof DonorApiError ? caught.message : "That didn’t work. Try again.",
            },
      );
    }
  };
  /**
   * After a sign-in from this flow (GH-871b). A new person's sign-in carried the join token
   * (`eventJoinToken`) and the server made them a member with the account — `membershipJoined` says
   * so — so there is nothing to join; the card reads the membership to welcome them by number.
   * Anyone else (an existing account) joins as before.
   */
  const settle = async (issued: DonorSessionResponse) => {
    if (!joinToken) return;
    if (!issued.membershipJoined) return join(issued.token);
    setState({ kind: "joining" });
    try {
      const member = (await fetchDonorMembership(issued.token))?.member ?? null;
      setState(
        member
          ? { kind: "joined", number: member.number, founding: member.founding }
          : { kind: "already" },
      );
    } catch {
      // The sign-in already made them a member; a failed read only loses the number.
      setState({ kind: "already" });
    }
  };
  return { state, join, settle };
}

export type EventJoin = ReturnType<typeof useEventJoin>;

/**
 * "You're invited — join GiveHere".
 *
 * `signInBelow` is for a host that already draws its own sign-in under this card (`/c/<eventId>`
 * for a stranger at a live event): one form on a page, not two, and signing in there joins. Every
 * other host gets a button that opens the sign-in sheet when nobody is signed in.
 */
export function EventJoinCard({
  joinToken,
  token,
  join,
  signInBelow = false,
  continueHref,
  onSession,
}: {
  joinToken: string | null;
  token: string | null;
  join: EventJoin;
  signInBelow?: boolean;
  /**
   * The event page, for a host that is not already showing a way there (GH-864g). A join that
   * came from the event's QR lands the person on that event: once the join is answered — joined,
   * already a member, or on the waitlist — the card offers "Continue to the event". A link, not a
   * timed redirect, for CheckInPage's reason: the person reads the confirmation first.
   */
  continueHref?: string | undefined;
  /** The host learns of a session the sheet created, before the join is sent. */
  onSession?: (issued: DonorSessionResponse) => void;
}) {
  const pulse = useMembershipPulse(joinToken !== null);
  const [signingIn, setSigningIn] = useState(false);
  const { state } = join;
  if (!joinToken || pulse === undefined || pulse === null || state.kind === "gone") return null;
  const answered =
    state.kind === "joined" || state.kind === "already" || state.kind === "waitlisted";

  return (
    <section className="event-join" aria-labelledby="event-join-title" aria-live="polite">
      {state.kind === "joined" ? (
        <>
          <h2 id="event-join-title" className="event-join__title">
            {MEMBERSHIP_COPY.welcomeMember(grouped(state.number))}
          </h2>
          {state.founding && (
            <p className="membership-page__keyline">{MEMBERSHIP_COPY.foundingMember}</p>
          )}
        </>
      ) : state.kind === "already" ? (
        <h2 id="event-join-title" className="event-join__title">
          You’re already a member.
        </h2>
      ) : state.kind === "waitlisted" ? (
        <>
          <h2 id="event-join-title" className="event-join__title">
            {MEMBERSHIP_COPY.eventCodeExpired}
          </h2>
          <p className="event-join__body">{MEMBERSHIP_COPY.eventCodeExpiredBody}</p>
        </>
      ) : (
        <>
          <h2 id="event-join-title" className="event-join__title">
            {MEMBERSHIP_COPY.invitedYouAnonymous}
          </h2>
          {signInBelow && !token ? (
            <p className="event-join__body">Sign in below to join GiveHere.</p>
          ) : (
            <button
              type="button"
              className="pay pay--outline"
              disabled={state.kind === "joining"}
              onClick={() => (token ? void join.join(token) : setSigningIn(true))}
            >
              {state.kind === "joining" ? "Joining…" : "Join GiveHere"}
            </button>
          )}
          {state.kind === "failed" && (
            <p className="notice notice--error" role="alert">
              {state.message}
            </p>
          )}
        </>
      )}

      {answered && continueHref && (
        <a className="pay pay--outline landing__give" href={continueHref}>
          Continue to the event
        </a>
      )}

      {signingIn && (
        <SignInSheet
          heading="Sign in to join"
          lede="You join GiveHere once you’re signed in."
          eventJoinToken={joinToken}
          onIssued={(issued) => {
            setSigningIn(false);
            onSession?.(issued);
            return join.settle(issued);
          }}
          onDismiss={() => setSigningIn(false)}
        />
      )}
    </section>
  );
}
